import {
  ArrowLeft,
  CheckmarkFilled,
  Help,
  Redo,
  RuleDraft,
} from '@carbon/icons-react';
import * as React from 'react';
import { Button } from 'vet-bones/design-system/components';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { TEST_IDS } from 'src/constants/testIds';
import { CreateProjectStep } from 'src/containers/CreateProjectOverlay/CreateProjectStep';
import { useCreateProject } from 'src/containers/CreateProjectOverlay/hooks/useCreateProject';
import {
  CreateProjectStepValue,
  useCreateProjectState,
} from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';

export const CreateProjectSteps: React.VFC = () => {
  const createProject = useCreateProject();
  const {
    currentStep,
    name,
    didGenerateInitialQuestions,
    isGeneratingQuestions,
    handleBack,
    handleGenerateQuestions,
    setCurrentStep,
  } = useCreateProjectState();
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const steps = React.useMemo(
    () =>
      [
        {
          icon: RuleDraft,
          text: copy.components.createProjectOverlay.steps.projectScoping,
          value: CreateProjectStepValue.PROJECT_SCOPING,
          onClick: () => setCurrentStep(CreateProjectStepValue.PROJECT_SCOPING),
        },
      ].concat(
        useVoiceboxSuggestions
          ? [
              {
                icon: Help,
                text: copy.components.createProjectOverlay.steps
                  .competencyQuestions,
                value: CreateProjectStepValue.COMPETENCY_QUESTIONS,
                onClick: () => {
                  if (!didGenerateInitialQuestions) {
                    handleGenerateQuestions();
                  }
                  setCurrentStep(CreateProjectStepValue.COMPETENCY_QUESTIONS);
                },
              },
            ]
          : []
      ),
    [
      didGenerateInitialQuestions,
      handleGenerateQuestions,
      setCurrentStep,
      useVoiceboxSuggestions,
    ]
  );
  const currentIdx = steps.findIndex((step) => step.value === currentStep);

  return (
    <div className="sd-create-project-steps">
      <CreateProjectStep
        icon={ArrowLeft}
        iconSize="lg"
        onClick={handleBack}
        rightBorder={currentIdx === 0}
        testId={TEST_IDS.createProjectOverlay.steps.backButton}
      />
      {steps.map(({ icon, onClick, text, value }, stepIdx) => (
        <CreateProjectStep
          key={value}
          active={stepIdx === currentIdx}
          disabled={!name.trim() && stepIdx > currentIdx}
          icon={stepIdx < currentIdx ? CheckmarkFilled : icon}
          leftBorder={stepIdx === currentIdx + 1}
          onClick={stepIdx === currentIdx ? undefined : onClick}
          rightBorder={stepIdx === currentIdx - 1}
          stepIdx={`0${stepIdx + 1}`}
          testId={TEST_IDS.createProjectOverlay.steps.stepButton(
            value,
            stepIdx
          )}
          text={text}
        />
      ))}
      <CreateProjectStep leftBorder={currentIdx === steps.length - 1}>
        <Button
          disabled={isGeneratingQuestions || !name.trim()}
          onClick={() => createProject()}
          rightIcon={Redo}
          size="md"
          testId={TEST_IDS.createProjectOverlay.steps.submitButton}
          variant="secondary"
        >
          {copy.components.createProjectOverlay.steps.createProject}
        </Button>
      </CreateProjectStep>
    </div>
  );
};
